import { Button, Dialog, DialogContent, DialogTitle, Grid, makeStyles } from '@material-ui/core';
import { defer } from '@thalesrc/js-utils';
import React, { useCallback, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import { FamilyHistoryModel, saveFamilyHistory, updateFamilyHistory } from 'api/hs/examination/family-history';
import Icd10Selection from 'Common/Form/Icd10Selection';
import { Icd10CacheContext } from 'Common/Form/Icd10Selection/icd10.context';
import Select from 'Common/Form/Select';
import TextField from 'Common/Form/TextField';
import { AlertContext } from 'context/alert.context';
import { SelectOptionsContext } from 'context/select-options.context';
import { useAsyncEffect } from 'utils';

const useStyles = makeStyles(theme => ({
  dialog: {
    width: '100%',
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  sideGrid: {
    display: 'grid',
    gridTemplateColumns: '100%',
    gridTemplateRows: '1fr auto',
  },
  dialogPaper: {
    width: '50vw',
  },
  dialogScroll: {
    alignItems: 'flex-start',
  },
}));

interface Props {
  open: boolean;

  onClose(success: boolean): void;

  data: FamilyHistoryModel;
}

export default function FamilyHistoryForm({ open, onClose, data }: Props) {
  const classes = useStyles();
  const { reset, setValue, trigger, ...formMethods } = useForm();
  const { showMessage } = useContext(AlertContext);
  const { employeeId, examinationId } = useParams<{ employeeId: string; examinationId: string }>();
  const { getOptions } = useContext(Icd10CacheContext);
  const { bloodRelation } = useContext(SelectOptionsContext);
  const handleSubmit = useCallback(
    async values => {
      try {
        if (!data?.id) {
          await saveFamilyHistory({
            employeeId,
            examinationId,
            description: values.description,
            bloodRelationDefinitionId: values.bloodRelationDefinitionId,
            familyHistoryIcd10s: values.icd10.map((id: number) => ({ icd10: id })),
          });
          showMessage('Ekleme işlemi başarılı.', 'success', 'info');
        } else {
          await updateFamilyHistory({
            id: data.id,
            employeeId,
            examinationId,
            description: values.description,
            bloodRelationDefinitionId: values.bloodRelationDefinitionId,
            familyHistoryIcd10s: values.icd10.map((id: number) => ({ icd10: id })),
          });
          showMessage('Güncelleme işlemi başarılı.', 'success', 'info');
        }
        onClose(true);
      } catch (err) {
        showMessage('İşlem sırasında sorun oluştu.', 'error', 'info');
        onClose(false);
      }
    },
    [data, onClose, showMessage, employeeId, examinationId]
  );

  useAsyncEffect(async () => {
    if (!open) {
      return;
    }
    await defer();

    if (data) {
      getOptions(...data.icd10s);
      setValue('icd10', data.icd10s);
      setValue('description', data.description);
      setValue('bloodRelationDefinitionId', data.bloodRelationDefinitionId);
      trigger();
    } else {
      reset({
        icd10: [],
        description: '' as any,
        bloodRelationDefinitionId: null,
      });
    }
  }, [data, setValue, reset, trigger, open]);

  return (
    <Dialog
      PaperProps={{ style: { maxWidth: 600 } }}
      open={open}
      keepMounted
      onClose={onClose.bind(null, false)}
      classes={{ paper: classes.dialogPaper, scrollPaper: classes.dialogScroll }}>
      <DialogTitle className={classes.dialogTitle}>{data?.id ? 'Soy Geçmiş Düzenleme' : 'Soy Geçmiş Ekle'}</DialogTitle>
      <DialogContent>
        <FormProvider {...{ ...formMethods, reset, setValue, trigger }}>
          <form noValidate className="mt-2" onSubmit={formMethods.handleSubmit(handleSubmit)}>
            <Grid container spacing={1}>
              <Grid item md={12} sm={12} lg={12}>
                <Select required name="bloodRelationDefinitionId" options={bloodRelation} label="Yakınlık" />
              </Grid>
              <Grid item md={12} sm={12} lg={12}>
                <Icd10Selection name="icd10" />
              </Grid>
              <Grid item md={12} sm={12} lg={12}>
                <TextField variant="outlined" rows={3} name="description" label="Açıklama" />
              </Grid>
              <Grid item md={12} className="text-right">
                <Button variant="contained" type="submit" color="primary" className="mr-2">
                  {data?.id ? 'Güncelle' : 'Kaydet'}
                </Button>
                <Button variant="contained" type="button" onClick={onClose.bind(null, false)}>
                  Vazgeç
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
